import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Loader2, Moon, Sun, Check } from "lucide-react";
import { toast } from "sonner";

function Script({ it, onData }) {
  const [tab, setTab] = useState("day");
  const [busy, setBusy] = useState(false);
  const text = tab === "day" ? it.long_script : it.sleep_script;
  const approve = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/long-installations/${it.id}/approve`); onData(data); toast.success(`Making audio for ${it.title}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't approve"); }
    setBusy(false);
  };
  return (
    <article className="rounded-3xl border border-border bg-card/40 p-6 sm:p-8 mb-8" data-testid={`review-${it.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-serif text-3xl">{it.title}</h2>
        <div className="inline-flex rounded-full border border-border p-1">
          {[["day", Sun, "Day"], ["sleep", Moon, "Sleep"]].map(([k, Icon, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`review-tab-${k}-${it.id}`}
              className={`px-4 py-1.5 rounded-full text-xs flex items-center gap-1.5 transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}><Icon className="w-3.5 h-3.5" /> {l}</button>
          ))}
        </div>
      </div>
      <div className="font-serif text-lg leading-[1.9] text-foreground/90 space-y-5 max-w-prose" data-testid={`review-text-${it.id}`}>
        {(text || "").split(/\n\s*\n/).map((p, i) => <p key={i}>{p}</p>)}
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-8 pt-6 border-t border-border">
        {it.long_status === "ready" ? <span className="text-sm text-teal-400 flex items-center gap-1"><Check className="w-4 h-4" /> Live · day {it.long_duration} · sleep {it.sleep_duration}</span>
          : it.long_status === "rendering" ? <span className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Making audio…</span>
          : <Button className="rounded-full" onClick={approve} disabled={busy} data-testid={`review-approve-${it.id}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Approve & make audio"}</Button>}
        <Link to="/admin" className="text-sm text-muted-foreground hover:text-foreground">Edit in Admin →</Link>
      </div>
    </article>
  );
}

export default function ScriptReview() {
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const load = () => api.get("/admin/long-installations").then((r) => setData(r.data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); setData({ items: [] }); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!data?.items?.some((i) => i.long_status === "rendering")) return;
    const t = setInterval(load, 10000); return () => clearInterval(t);
  }, [data]);
  const items = (data?.items || []).filter((i) => i.long_script);
  return (
    <div className="min-h-screen bg-background" data-testid="script-review">
      <Navbar />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">For your review</p>
        <h1 className="font-serif text-4xl sm:text-5xl mb-3">Full-length installation scripts</h1>
        <p className="text-muted-foreground mb-10">Read each Day and Sleep version. When one feels right, approve it and she'll voice it.</p>
        {denied && <p className="text-sm text-muted-foreground" data-testid="script-review-denied">This page is for Xcelio admins only.</p>}
        {!data ? <Loader2 className="w-5 h-5 animate-spin" /> : items.map((it) => <Script key={it.id} it={it} onData={setData} />)}
      </main>
    </div>
  );
}
